import React, { useState, useEffect } from 'react';
import { 
  Filter, 
  X, 
  Plus, 
  Download, 
  BarChart3, 
  Table as TableIcon,
  ChevronDown,
  ChevronUp,
  Search,
  RefreshCw,
  Database
} from 'lucide-react';
import DataTable from './DataTable';
import ChartContainer from './ChartContainer';
import StatsBar from './StatsBar';
import AthleteDetailModal from './AthleteDetailModal';

export default function ManualExplorer() {
  const [filters, setFilters] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [chartType, setChartType] = useState('bar');
  const [districts, setDistricts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [loadingAthlete, setLoadingAthlete] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('name');
  
  // Data source selection
  const [dataSource, setDataSource] = useState('internal');
  const [externalConnections, setExternalConnections] = useState([]);
  const [importedDatasets, setImportedDatasets] = useState([]);
  
  // Available filter types
  const filterTypes = [
    { id: 'district', label: 'District', type: 'select' },
    { id: 'gender', label: 'Gender', type: 'select' },
    { id: 'age', label: 'Age Range', type: 'range' },
    { id: 'height', label: 'Height (cm)', type: 'range' },
    { id: 'weight', label: 'Weight (kg)', type: 'range' },
    { id: 'vertical_jump', label: 'Vertical Jump (cm)', type: 'range' },
    { id: 'sprint_30m', label: '30m Sprint (sec)', type: 'range' },
  ];

  useEffect(() => {
    // Load districts on mount
    loadDistricts();
    // Load external connections
    loadExternalConnections();
    // Load imported datasets
    loadImportedDatasets();
    // Load initial data
    loadData();
  }, []);

  useEffect(() => {
    // Reload data when page changes or data source changes
    loadData();
  }, [page, dataSource]);

  const loadExternalConnections = async () => {
    try {
      const userId = localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000';
      const response = await fetch('/api/external-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'list-connections',
          data: { userId }
        })
      });
      const result = await response.json();
      if (result.success) {
        // Only show normalized connections in dropdown
        setExternalConnections(result.connections.filter(c => c.status === 'active' && c.is_normalized === true));
      }
    } catch (error) {
      console.error('Failed to load external connections:', error);
    }
  };

  const loadImportedDatasets = async () => {
    try {
      const userId = localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000';
      const response = await fetch('/api/data-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'list-imports',
          data: { userId }
        })
      });
      const result = await response.json();
      if (result.success) {
        setImportedDatasets(result.imports.filter(i => i.status === 'completed'));
      }
    } catch (error) {
      console.error('Failed to load imported datasets:', error);
    }
  };

  const loadDistricts = async () => {
    try {
      const response = await fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'api.execute',
          data: {
            actionKey: 'geography.districts',
            params: {},
            user: { role: 'admin' }
          }
        })
      });
      const result = await response.json();
      if (result.success) {
        setDistricts(result.data);
      }
    } catch (error) {
      console.error('Failed to load districts:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    console.log('🔍 Loading data for source:', dataSource);
    try {
      // Check if querying external database
      if (dataSource.startsWith('external_')) {
        console.log('📊 Querying external database...');
        const connectionId = dataSource.replace('external_', '');
        const connection = externalConnections.find(c => c.id === connectionId);
        
        if (!connection) {
          console.error('❌ External connection not found:', connectionId);
          console.log('Available connections:', externalConnections);
          setLoading(false);
          return;
        }
        
        console.log('✅ Found connection:', connection.name);

        // First, discover schema to get table name
        const userId = localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000';
        const schemaResponse = await fetch('/api/external-db', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'discover-schema',
            data: { connectionId, userId }
          })
        });

        const schemaResult = await schemaResponse.json();
        
        if (!schemaResult.success || !schemaResult.schema.tables || schemaResult.schema.tables.length === 0) {
          console.error('No tables found in external database');
          setLoading(false);
          return;
        }

        // Use first table (or could let user select)
        const tableName = schemaResult.schema.tables[0];

        // Build filters for external DB
        const externalFilters = {};
        filters.forEach(filter => {
          if (filter.value) {
            if (filter.type === 'age' || filter.type === 'height' || filter.type === 'weight') {
              externalFilters[filter.type] = { min: filter.value.min, max: filter.value.max };
            } else {
              externalFilters[filter.type] = filter.value;
            }
          }
        });

        // Query external database
        const response = await fetch('/api/external-db', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'query',
            data: {
              connectionId,
              userId,
              tableName,
              page,
              pageSize,
              filters: externalFilters
            }
          })
        });

        const result = await response.json();
        
        if (result.success) {
          setData(result.data || []);
          setTotalCount(result.totalRows || result.data.length);
          calculateStats(result.data || []);
        }
      }
      // Check if querying imported dataset
      else if (dataSource.startsWith('import_')) {
        const datasetId = dataSource.replace('import_', '');
        const dataset = importedDatasets.find(d => d.id === datasetId);
        
        if (!dataset) {
          console.error('Dataset not found');
          setLoading(false);
          return;
        }

        // Query the import table directly
        const response = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'api.execute',
            data: {
              actionKey: 'custom.query',
              query: `SELECT * FROM ${dataset.table_name} WHERE validation_status = 'valid' LIMIT ${pageSize} OFFSET ${(page - 1) * pageSize}`,
              user: { role: 'admin' }
            }
          })
        });

        const result = await response.json();
        
        if (result.success) {
          setData(result.data || []);
          setTotalCount(dataset.valid_records || 0);
          calculateStats(result.data || []);
        }
      } else {
        // Build params from filters for internal database
        const params = { 
          limit: pageSize, 
          offset: (page - 1) * pageSize 
        };
        
        // Add search query if present
        if (searchQuery.trim()) {
          if (searchType === 'name') {
            params.search_name = searchQuery.trim();
          } else if (searchType === 'id') {
            params.search_id = searchQuery.trim();
          }
        }
        
        filters.forEach(filter => {
          if (filter.type === 'district' && filter.value) {
            params.district_id = filter.value;
          } else if (filter.type === 'gender' && filter.value) {
            params.gender = filter.value;
          } else if (filter.type === 'age' && filter.value) {
            params.min_age = filter.value.min;
            params.max_age = filter.value.max;
          } else if (filter.type === 'height' && filter.value) {
            params.min_height = filter.value.min;
            params.max_height = filter.value.max;
          }
        });

        // Fetch athletes data with count
        const response = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'api.execute',
            data: {
              actionKey: 'athletes.list',
              params: { ...params, includeCount: true },
              user: { role: 'admin' }
            }
          })
        });

        const result = await response.json();
        
        if (result.success) {
          setData(result.data);
          setTotalCount(result.total || result.data.length);
          calculateStats(result.data);
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (athletes) => {
    if (athletes.length === 0) {
      setStats(null);
      return;
    }

    const maleCount = athletes.filter(a => a.gender === 'Male').length;
    const femaleCount = athletes.filter(a => a.gender === 'Female').length;
    const avgHeight = athletes.reduce((sum, a) => sum + parseFloat(a.height_cm || 0), 0) / athletes.length;
    const avgWeight = athletes.reduce((sum, a) => sum + parseFloat(a.weight_kg || 0), 0) / athletes.length;
    const avgVJ = athletes.reduce((sum, a) => sum + parseFloat(a.vertical_jump_cm || 0), 0) / athletes.length;

    setStats({
      total: athletes.length,
      male: maleCount,
      female: femaleCount,
      avgHeight: avgHeight.toFixed(1),
      avgWeight: avgWeight.toFixed(1),
      avgVerticalJump: avgVJ.toFixed(1)
    });
  };

  const addFilter = (filterType) => {
    const newFilter = {
      id: Date.now(),
      type: filterType.id,
      label: filterType.label,
      value: null
    };
    setFilters([...filters, newFilter]);
  };

  const removeFilter = (filterId) => {
    setFilters(filters.filter(f => f.id !== filterId));
  };

  const updateFilter = (filterId, value) => {
    setFilters(filters.map(f => f.id === filterId ? { ...f, value } : f));
  };

  const clearAllFilters = () => {
    setFilters([]);
    loadData();
  };

  const exportData = () => {
    // Convert to CSV
    const headers = ['Name', 'Gender', 'Age', 'Height', 'Weight', 'District', 'School'];
    const rows = data.map(athlete => [
      `${athlete.first_name} ${athlete.last_name}`,
      athlete.gender,
      new Date().getFullYear() - new Date(athlete.date_of_birth).getFullYear(),
      athlete.height_cm,
      athlete.weight_kg,
      athlete.district_name,
      athlete.school_name
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `athletes_${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="grid grid-cols-12 gap-6 h-[calc(100vh-180px)]">
      {/* LEFT PANEL - Filters (40%) */}
      <div className="col-span-5 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-blue-400" />
            <h2 className="text-xl font-bold text-white">Filters</h2>
          </div>
          {filters.length > 0 && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-red-400 hover:text-red-300 flex items-center space-x-1"
            >
              <X className="w-4 h-4" />
              <span>Clear All</span>
            </button>
          )}
        </div>

        {/* Active Filters */}
        <div className="space-y-4 mb-6">
          {filters.map(filter => (
            <FilterControl
              key={filter.id}
              filter={filter}
              districts={districts}
              onUpdate={(value) => updateFilter(filter.id, value)}
              onRemove={() => removeFilter(filter.id)}
            />
          ))}
        </div>

        {/* Add Filter Dropdown */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Add Filter
          </label>
          <select
            onChange={(e) => {
              if (e.target.value) {
                const filterType = filterTypes.find(ft => ft.id === e.target.value);
                addFilter(filterType);
                e.target.value = '';
              }
            }}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select filter type...</option>
            {filterTypes.map(ft => (
              <option key={ft.id} value={ft.id}>{ft.label}</option>
            ))}
          </select>
        </div>

        {/* Stats Summary */}
        {stats && (
          <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Result Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Showing:</span>
                <span className="text-white font-semibold">{stats.total} of {totalCount.toLocaleString()}</span>
              </div>
              {filters.length > 0 && (
                <div className="text-xs text-blue-400 mb-2">
                  {filters.length} filter{filters.length > 1 ? 's' : ''} applied
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-400">Male / Female:</span>
                <span className="text-white">{stats.male} / {stats.female}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Avg Height:</span>
                <span className="text-white">{stats.avgHeight} cm</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Avg Weight:</span>
                <span className="text-white">{stats.avgWeight} kg</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Avg Vertical Jump:</span>
                <span className="text-white">{stats.avgVerticalJump} cm</span>
              </div>
            </div>
          </div>
        )}

        {/* Apply Button */}
        <button
          onClick={loadData}
          disabled={loading}
          className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Loading...</span>
            </>
          ) : (
            <>
              <Search className="w-5 h-5" />
              <span>Apply Filters</span>
            </>
          )}
        </button>
      </div>

      {/* RIGHT PANEL - Results (60%) */}
      <div className="col-span-7 space-y-6">
        {/* Data Source Selector */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1">
              <Database className="w-5 h-5 text-blue-400" />
              <div className="flex-1">
                <label className="block text-xs text-slate-400 mb-1">Data Source</label>
                <select
                  value={dataSource}
                  onChange={(e) => {
                    setDataSource(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="internal">🏠 Internal SRI Database (10,000 athletes)</option>
                  {importedDatasets.length > 0 && (
                    <optgroup label="Imported Datasets">
                      {importedDatasets.map(dataset => (
                        <option key={dataset.id} value={`import_${dataset.id}`}>
                          📊 {dataset.name} ({dataset.valid_records} records)
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {externalConnections.length > 0 && (
                    <optgroup label="External Databases (Live)">
                      {externalConnections.map(conn => (
                        <option key={conn.id} value={`external_${conn.id}`}>
                          {conn.db_type === 'postgresql' && '🐘'}
                          {conn.db_type === 'mysql' && '🐬'}
                          {conn.db_type === 'sqlserver' && '🗄️'}
                          {' '}{conn.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            </div>
            {dataSource !== 'internal' && (
              <div className={`ml-4 px-3 py-1 rounded-lg text-xs ${
                dataSource.startsWith('import_')
                  ? 'bg-green-500/20 border border-green-500/30 text-green-400'
                  : 'bg-purple-500/20 border border-purple-500/30 text-purple-400'
              }`}>
                {dataSource.startsWith('import_') ? 'Imported Dataset' : 'External DB'}
              </div>
            )}
          </div>
        </div>

        {/* Quick Search Bar */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
          <div className="flex items-center space-x-3">
            <Search className="w-5 h-5 text-slate-400" />
            <div className="flex-1 flex items-center space-x-3">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="name">Name</option>
                <option value="id">Athlete ID</option>
              </select>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1);
                    loadData();
                  }
                }}
                placeholder={searchType === 'name' ? 'Search by athlete name...' : 'Search by athlete ID...'}
                className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => {
                  setPage(1);
                  loadData();
                }}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>Search</span>
              </button>
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setPage(1);
                    setTimeout(() => loadData(), 100);
                  }}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          {searchQuery && (
            <div className="mt-2 text-sm text-blue-400">
              Searching for {searchType}: "{searchQuery}"
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowChart(!showChart)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                showChart ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
            </button>
            <button
              className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg"
            >
              <TableIcon className="w-4 h-4" />
            </button>
          </div>
          
          <button
            onClick={exportData}
            disabled={data.length === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
        </div>

        {/* Chart (if enabled) */}
        {showChart && data.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
            <ChartContainer data={data} type={chartType} />
          </div>
        )}

        {/* Data Table */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 overflow-hidden">
          <DataTable 
            data={data} 
            loading={loading} 
            onAthleteClick={(athlete) => setSelectedAthlete(athlete)}
          />
        </div>

        {/* Pagination */}
        {totalCount > pageSize && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-400">
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()} athletes
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-white px-3">
                  Page {page} of {Math.ceil(totalCount / pageSize)}
                </span>
                <button
                  onClick={() => setPage(Math.min(Math.ceil(totalCount / pageSize), page + 1))}
                  disabled={page >= Math.ceil(totalCount / pageSize)}
                  className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Athlete Detail Modal */}
      {selectedAthlete && (
        <AthleteDetailModal
          athlete={selectedAthlete}
          allAthletes={data}
          onClose={() => setSelectedAthlete(null)}
          loading={loadingAthlete}
          dataSource={dataSource}
        />
      )}
    </div>
  );
}

// Filter Control Component
function FilterControl({ filter, districts, onUpdate, onRemove }) {
  if (filter.type === 'district') {
    return (
      <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-300">{filter.label}</label>
          <button onClick={onRemove} className="text-slate-400 hover:text-red-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <select
          value={filter.value || ''}
          onChange={(e) => onUpdate(e.target.value)}
          className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Districts</option>
          {districts.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>
    );
  }

  if (filter.type === 'gender') {
    return (
      <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-300">{filter.label}</label>
          <button onClick={onRemove} className="text-slate-400 hover:text-red-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onUpdate('Male')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter.value === 'Male' ? 'bg-blue-600 text-white' : 'bg-slate-600 text-slate-300'
            }`}
          >
            Male
          </button>
          <button
            onClick={() => onUpdate('Female')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter.value === 'Female' ? 'bg-pink-600 text-white' : 'bg-slate-600 text-slate-300'
            }`}
          >
            Female
          </button>
        </div>
      </div>
    );
  }

  // Range filters
  return (
    <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-slate-300">{filter.label}</label>
        <button onClick={onRemove} className="text-slate-400 hover:text-red-400">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          placeholder="Min"
          value={filter.value?.min || ''}
          onChange={(e) => onUpdate({ ...filter.value, min: e.target.value })}
          className="px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="number"
          placeholder="Max"
          value={filter.value?.max || ''}
          onChange={(e) => onUpdate({ ...filter.value, max: e.target.value })}
          className="px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}
